import { useCallback, useEffect, useMemo, useState } from 'react'
import type { CheckedInExhibit } from '@/hooks/useEntryIntake'
import { parseCheckInTimestamp, RECENT_EVENTS_STORAGE_KEY } from '@/features/intake/utils'

type UseRecentCheckInsOptions = {
  events: CheckedInExhibit[]
  showId?: string | null
  defaultCollapsed?: boolean
  listIdPrefix?: string
  storageKey?: string
  limit?: number
}

export function useRecentCheckIns({
  events,
  showId = null,
  defaultCollapsed = true,
  listIdPrefix = 'recent-checkins',
  storageKey = RECENT_EVENTS_STORAGE_KEY,
  limit = 3,
}: UseRecentCheckInsOptions) {
  const normalizedEvents = useMemo(() => {
    if (!events.length) return []
    return [...events]
      .sort((a, b) => parseCheckInTimestamp(b.checkedInAt) - parseCheckInTimestamp(a.checkedInAt))
      .slice(0, limit)
  }, [events, limit])

  const resolvedStorageKey = useMemo(() => {
    if (!storageKey) return null
    if (typeof showId === 'string' && showId.length > 0) {
      return `${storageKey}/${showId}`
    }
    return storageKey
  }, [showId, storageKey])

  const readPreference = useCallback(() => {
    if (typeof window === 'undefined') return defaultCollapsed
    if (!resolvedStorageKey) return defaultCollapsed
    try {
      const stored = window.localStorage.getItem(resolvedStorageKey)
      if (stored === '0') return false
      if (stored === '1') return true
    } catch {
      // ignore storage errors
    }
    return defaultCollapsed
  }, [defaultCollapsed, resolvedStorageKey])

  const [collapsed, setCollapsed] = useState<boolean>(() => readPreference())

  useEffect(() => {
    setCollapsed(readPreference())
  }, [readPreference])

  const toggleCollapsed = useCallback(() => {
    setCollapsed(prev => {
      const next = !prev
      if (resolvedStorageKey && typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(resolvedStorageKey, next ? '1' : '0')
        } catch {
          // ignore storage errors
        }
      }
      return next
    })
  }, [resolvedStorageKey])

  const listId = useMemo(() => {
    const prefix = listIdPrefix || 'recent-checkins'
    const suffix = showId ?? 'default'
    return `${prefix}-${suffix}`
  }, [listIdPrefix, showId])

  return {
    events: normalizedEvents,
    collapsed,
    listId,
    toggleCollapsed,
  }
}
